import React, { useEffect, useState } from 'react';
import { LayoutGrid, List, Rss, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// ── What each layout value controls ──────────────────────────────────────────
//
//  'overview'  → Home page shows tile view (default)
//  'detailed'  → Home page shows list view
//  'minimal'   → Land on Feeds page instead of Home
//
// Persisted to user_preferences.dashboard_layout.
// home_view_mode in localStorage is also set for instant effect without a DB read.

const LAYOUT_OPTIONS = [
  {
    value: 'overview' as const,
    label: 'Tile view',
    description: 'Home shows a card for each source with live content previews.',
    icon: LayoutGrid,
    badge: 'Default',
    badgeClass: 'bg-emerald-100 text-emerald-800',
  },
  {
    value: 'detailed' as const,
    label: 'List view',
    description: 'Home shows all sources as a compact, scannable list.',
    icon: List,
    badge: 'Compact',
    badgeClass: 'bg-blue-100 text-blue-800',
  },
  {
    value: 'minimal' as const,
    label: 'Feeds first',
    description: 'Skip Home — land directly on your Curated feed every time.',
    icon: Rss,
    badge: 'Focus mode',
    badgeClass: 'bg-purple-100 text-purple-800',
  },
];

type LayoutValue = 'overview' | 'detailed' | 'minimal';

interface DashboardLayoutSwitcherProps {
  currentLayout?: string;
  onLayoutChange?: (layout: string) => void;
}

export const DashboardLayoutSwitcher: React.FC<DashboardLayoutSwitcherProps> = ({
  currentLayout,
  onLayoutChange,
}) => {
  const { savePreferences, preferences } = useUserPreferences();
  const navigate = useNavigate();

  // Local state drives visual selection immediately — no waiting for a DB round-trip
  const [selected, setSelected] = useState<LayoutValue>(() => {
    const v = currentLayout ?? preferences?.dashboard_layout ?? 'overview';
    return (['overview', 'detailed', 'minimal'] as LayoutValue[]).includes(v as LayoutValue)
      ? (v as LayoutValue)
      : 'overview';
  });
  const [saving, setSaving] = useState(false);

  // Sync if the parent's prop loads asynchronously after first render
  useEffect(() => {
    const v = currentLayout ?? preferences?.dashboard_layout;
    if (v && (['overview', 'detailed', 'minimal'] as string[]).includes(v) && v !== selected) {
      setSelected(v as LayoutValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLayout, preferences?.dashboard_layout]);

  const handleSelect = async (value: LayoutValue) => {
    // Update visual immediately
    setSelected(value);

    // Apply effect right now via localStorage so the home page picks it up instantly
    if (value === 'overview') {
      localStorage.setItem('home_view_mode', 'tile');
    } else if (value === 'detailed') {
      localStorage.setItem('home_view_mode', 'list');
    }

    // Persist to DB
    setSaving(true);
    try {
      const base = preferences ?? {
        primary_use_case: 'personal',
        platform_priority: [] as string[],
        dashboard_layout: 'overview',
        content_filters: [] as string[],
        monitoring_keywords: [] as string[],
        completed_onboarding: true,
      };
      await savePreferences({ ...base, dashboard_layout: value });
      onLayoutChange?.(value);
      const label = LAYOUT_OPTIONS.find((o) => o.value === value)?.label ?? value;
      toast.success(`Layout set to "${label}"`);
    } catch {
      toast.error('Could not save layout preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Dashboard layout</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          Choose how the app looks when you open it.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {LAYOUT_OPTIONS.map((layout) => {
          const Icon = layout.icon;
          const isSelected = selected === layout.value;

          return (
            <button
              key={layout.value}
              type="button"
              onClick={() => handleSelect(layout.value)}
              disabled={saving}
              className={[
                'text-left rounded-xl border p-4 transition-all',
                isSelected
                  ? 'border-[#1A2E4A] bg-[#1A2E4A]/[0.04] ring-1 ring-[#1A2E4A]/20'
                  : 'border-border hover:border-[#1A2E4A]/40 hover:bg-muted/30',
                saving ? 'opacity-60 cursor-wait' : 'cursor-pointer',
              ].join(' ')}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className={`p-2 rounded-lg ${isSelected ? 'bg-[#1A2E4A]/10' : 'bg-muted'}`}>
                  <Icon className={`h-4 w-4 ${isSelected ? 'text-[#1A2E4A]' : 'text-muted-foreground'}`} />
                </div>
                {isSelected && (
                  <div className="shrink-0 rounded-full bg-[#1A2E4A] p-0.5">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>

              <p className={`text-sm font-medium mb-1 ${isSelected ? 'text-[#1A2E4A]' : ''}`}>
                {layout.label}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {layout.description}
              </p>
              <Badge className={`mt-2 text-[10px] py-0 px-1.5 ${layout.badgeClass}`}>
                {layout.badge}
              </Badge>
            </button>
          );
        })}
      </div>

      {selected === 'minimal' && (
        <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
          Feeds-first takes effect next time you go to Home. Or{' '}
          <button
            type="button"
            className="underline hover:text-foreground transition-colors"
            onClick={() => navigate('/dashboard/feeds')}
          >
            go to Feeds now ↗
          </button>
        </p>
      )}
    </div>
  );
};

export default DashboardLayoutSwitcher;
